import type { BrokerCapability } from "@/lib/brokerAuth";

// The Broker Portal hosts two platforms in one app — Commercial insurance and
// Surety — toggled by the product-line switcher in the top bar. This module is
// the single source of truth for that split: the product lines, each one's home
// route, its left-nav destinations, and (BRK-S090) the mapping from a pathname
// back to the line it belongs to.
//
// The active line is **derived from the route** (`productLineForPath`), not held
// as app state only the switcher writes. It used to be the latter, which meant
// anything that moved the URL without going through the switcher — a deep link,
// a hard reload, browser back/forward, a cross-line link, a second tab — left
// the pill and the left nav describing a platform the broker wasn't on. Routes
// that belong to neither platform (`/profile`, `/security`, Accounts) leave the
// line undecided, and the last derived line (persisted) stands.

/** The two platforms the portal hosts. */
export type ProductLine = "commercial" | "surety";

/** The platform a fresh session opens in. */
export const DEFAULT_PRODUCT_LINE: ProductLine = "commercial";

/** localStorage key the last derived line is persisted under — the fallback for
 *  line-agnostic routes, so a returning broker reopens where they left off. */
export const PRODUCT_LINE_STORAGE_KEY = "brk:productLine";

/** Every Surety route lives under this prefix, which is what makes the URL a
 *  reliable discriminator. Referenced instead of re-typed (`usePageTitle`). */
export const SURETY_PATH_PREFIX = "/surety";

/** The switcher's options, in display order. */
export const PRODUCT_LINES: ReadonlyArray<{ id: ProductLine; label: string }> =
  [
    { id: "commercial", label: "Commercial" },
    { id: "surety", label: "Surety" },
  ];

/** Each platform's landing route — where the switcher lands you on a change. */
export const PRODUCT_LINE_HOME: Record<ProductLine, string> = {
  commercial: "/portal",
  surety: "/surety/construction",
};

/** The human label for a line (used by the switcher trigger). */
export function productLineLabel(line: ProductLine): string {
  return PRODUCT_LINES.find((p) => p.id === line)?.label ?? "";
}

/**
 * One left-nav destination. `icon` keys into the SideNav icon map (icons stay
 * as JSX in SideNav so this stays plain data). `to` is the route (omitted for an
 * inert item). `end` marks a home link that should only be active on an exact
 * match. `capability` gates the item off a broker capability (never the raw
 * role) — omitted items always show. `inert` renders the item "for show" — a
 * non-actionable label (same look as an inactive link), used on the Surety
 * platform where only Bond Exchange is wired so far.
 */
export interface NavItemConfig {
  label: string;
  icon: string;
  to?: string;
  end?: boolean;
  capability?: BrokerCapability;
  inert?: boolean;
}

// Commercial: the existing insurance platform. Surety: the bond platform.
// Accounts (capability-gated) and the shared bottom items (Documents,
// Commissions, Support — owned by SideNav) appear in both.
const COMMERCIAL_NAV: NavItemConfig[] = [
  { to: "/portal", label: "API Exchange", icon: "exchange", end: true },
  { to: "/direct-markets", label: "Direct Markets", icon: "directMarkets" },
  {
    to: "/accounts",
    label: "Accounts",
    icon: "accounts",
    capability: "account:view",
  },
  { to: "/submissions", label: "Submissions", icon: "submissions" },
  { to: "/quotes", label: "Quotes", icon: "quotes" },
  { to: "/binders", label: "Binders", icon: "binders" },
  { to: "/policies", label: "Policies", icon: "policies" },
  { to: "/declinations", label: "Declinations", icon: "declinations" },
];

// Surety: the two bond exchanges (Construction, Commercial), Accounts,
// Submissions and Bonds are wired; every other destination renders "for show" (inert) until its Surety
// workflow exists. Direct Markets is Commercial-only (no direct surety markets),
// so it's absent.
const SURETY_NAV: NavItemConfig[] = [
  {
    to: "/surety/construction",
    label: "Construction Bond",
    icon: "construction",
  },
  {
    to: "/surety/commercial",
    label: "Commercial Bonds",
    icon: "commercialBond",
  },
  { label: "Bond Library", icon: "bondLibrary", inert: true },
  // Live as of BRK-S138 — the same own-scoped accounts the Commercial side
  // lists, with the bond profile behind each row.
  {
    to: "/surety/accounts",
    label: "Accounts",
    icon: "accounts",
    capability: "account:view",
  },
  // SUBMISSIONS lists bond REQUESTS — the brokerage's orders, drafts included
  // (BRK-S088) — and BONDS lists ISSUED bonds (BRK-S181). The two were swapped:
  // this item was a dead label while "Bonds" opened the orders list.
  { to: "/surety/submissions", label: "Submissions", icon: "submissions" },
  { label: "Quotes", icon: "quotes", inert: true },
  { to: "/surety/bonds", label: "Bonds", icon: "bonds" },
  { label: "Obligees", icon: "obligees", inert: true },
  { label: "Claims", icon: "claims", inert: true },
];

/** The primary nav destinations for each platform. */
export const PRODUCT_LINE_NAV: Record<ProductLine, NavItemConfig[]> = {
  commercial: COMMERCIAL_NAV,
  surety: SURETY_NAV,
};

// Routes that belong to neither platform, so landing on one must not rotate the
// broker's platform out from under them. Accounts is here because the top-bar
// header search reaches it from both platforms — opening an account found while
// working in Surety should not silently swing the whole portal to Commercial.
const LINE_AGNOSTIC_PREFIXES: readonly string[] = [
  "/profile",
  "/security",
  "/accounts",
];

/** True when `pathname` is `prefix` itself or a route nested under it. Compares
 *  whole segments, so "/sureties" is not under "/surety". */
function isUnder(pathname: string, prefix: string): boolean {
  return pathname === prefix || pathname.startsWith(`${prefix}/`);
}

/**
 * The platform a route belongs to, or `null` when the route says nothing about
 * it (the pre-login surface, the line-agnostic pages, an unknown path). The
 * Commercial side is read off `COMMERCIAL_NAV` rather than a parallel list, so
 * a new Commercial destination is classified by the act of adding it to the nav.
 */
export function productLineForPath(pathname: string): ProductLine | null {
  if (LINE_AGNOSTIC_PREFIXES.some((prefix) => isUnder(pathname, prefix))) {
    return null;
  }
  if (isUnder(pathname, SURETY_PATH_PREFIX)) return "surety";
  const isCommercial = COMMERCIAL_NAV.some(
    (item) => item.to !== undefined && isUnder(pathname, item.to),
  );
  return isCommercial ? "commercial" : null;
}

// The full-bleed landing ("exchange") pages of each platform: they own their own
// dark canvas and padding, so the shell must not wrap them in the padded reading
// column. Listed once here rather than restated in PortalShell.
const EXCHANGE_PATHS: readonly string[] = [
  PRODUCT_LINE_HOME.commercial,
  "/direct-markets",
  PRODUCT_LINE_HOME.surety,
  "/surety/commercial",
];

/** True for the full-bleed landing pages (see EXCHANGE_PATHS). */
export function isExchangePath(pathname: string): boolean {
  return EXCHANGE_PATHS.includes(pathname);
}

// Pages that fill the content area instead of the reading-width cap: the list
// pages, and the BaseForm record pages, whose tabs, multi-column fields and
// collapsible sidebar need the room. The shell's own side padding stays, so a
// wide page still keeps a small margin. `/accounts/new` is a form, not a record.
const WIDE_PATHS: readonly string[] = [
  "/submissions",
  "/quotes",
  "/binders",
  "/policies",
  "/declinations",
];
const WIDE_RECORD_PATH = /^\/accounts\/(?!new$)[^/]+$/;

/** True for the pages the shell renders without the reading-width cap. */
export function isWidePath(pathname: string): boolean {
  return WIDE_PATHS.includes(pathname) || WIDE_RECORD_PATH.test(pathname);
}
